'use client'

import { BookOpen } from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { MapArc, MapMarker, MarkerContent } from '@/components/ui/map'
import type { LngLat } from '@/lib/mock/mapLocations'
import { cn } from '@/utilities/ui'

/** Keep in sync with `MAP_TRANSITION_MS` in LocationAreaMap. */
const ROUTE_TRANSITION_MS = 900

/** Keep in sync with `MARKER_PILL_CLASS` in LocationAreaMap. */
const MARKER_PILL_CLASS =
  'locations-map-marker inline-flex max-w-[15rem] items-center gap-1 rounded-full border border-black/10 px-2.5 py-1.5 font-outfit text-base max-[575px]:text-sm font-medium shadow-[0px_6px_20px_0px_rgba(0,33,64,0.12)]'

export type LocationAreaMapUniversityRouteDirection =
  | 'campus-to-university'
  | 'university-to-campus'

export type LocationAreaMapUniversityRouteData = {
  campusCoordinate: LngLat
  routeAnimationId: number
  routeDirection?: LocationAreaMapUniversityRouteDirection
  universityCoordinate: LngLat
  universityName: string
}

export function LocationAreaMapUniversityRoute({
  campusCoordinate,
  routeAnimationId,
  routeDirection = 'university-to-campus',
  universityCoordinate,
  universityName,
}: LocationAreaMapUniversityRouteData) {
  const [progress, setProgress] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 1
      : 0,
  )
  const [isUniversityActive, setIsUniversityActive] = useState(false)
  const animationFrameRef = useRef<number | null>(null)
  const routeAnimationKey = `${routeAnimationId}:${routeDirection}:${universityCoordinate.join(',')}:${campusCoordinate.join(',')}`
  const from: LngLat =
    routeDirection === 'campus-to-university' ? campusCoordinate : universityCoordinate
  const to: LngLat =
    routeDirection === 'campus-to-university' ? universityCoordinate : campusCoordinate

  useEffect(() => {
    setIsUniversityActive(false)
  }, [routeAnimationKey])

  useEffect(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setProgress(1)
      return
    }

    setProgress(0)
    const startTime = performance.now()
    const animate = (timestamp: number) => {
      const nextProgress = Math.min(1, (timestamp - startTime) / ROUTE_TRANSITION_MS)
      setProgress(nextProgress)

      if (nextProgress >= 1) {
        animationFrameRef.current = null
        return
      }

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [routeAnimationKey])

  const arcData = useMemo(
    () => [
      {
        id: 'campus-route',
        from,
        to,
      },
    ],
    [from, to],
  )
  const [longitude, latitude] = universityCoordinate

  return (
    <>
      <MapArc
        curvature={0.25}
        data={arcData}
        interactive={false}
        paint={{
          'line-color': '#000000',
          'line-dasharray': [1, 2],
          'line-opacity': 0.75,
          'line-width': 2,
        }}
        progress={progress}
      />

      <MapMarker anchor="bottom" latitude={latitude} longitude={longitude}>
        <MarkerContent className="pointer-events-auto">
          <div
            className="relative flex flex-col items-center"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                setIsUniversityActive(false)
              }
            }}
            onFocus={() => setIsUniversityActive(true)}
            onMouseEnter={() => setIsUniversityActive(true)}
            onMouseLeave={() => setIsUniversityActive(false)}
          >
            <button
              aria-expanded={isUniversityActive}
              aria-label={`View ${universityName} on map`}
              className={cn(
                MARKER_PILL_CLASS,
                'cursor-pointer transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-cream',
                isUniversityActive && 'ring-2 ring-turquoise/40',
              )}
              type="button"
            >
              <BookOpen aria-hidden className="size-4 shrink-0 text-[#B4F0DD]" />
              <span className="truncate" title={universityName}>
                {universityName}
              </span>
            </button>
          </div>
        </MarkerContent>
      </MapMarker>
    </>
  )
}
